import React, { useState, useEffect } from 'react';
import {
    TrendingUp, AlertTriangle, MessageSquare,
    Calendar, ChevronDown, RefreshCw, Download,
    CheckCircle, XCircle, BookOpen, Users, X
} from 'lucide-react';
import { insightDigest } from '../../rag/insightDigest';
import { supabase } from '../../lib/supabase';
import { useHOA } from '../../contexts/HOAContext';
import { hoaProfile } from '../../lib/hoaProfile';

const InsightDigest = () => {
    const { hoa } = useHOA();
    const [digest, setDigest] = useState(null);
    const [windowDays, setWindowDays] = useState(30);
    const [loading, setLoading] = useState(true);
    const [lastGenerated, setLastGenerated] = useState(null);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') setSelectedTopic(null);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        if (windowDays !== 'custom') {
            generateDigest();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowDays, hoa?.id]);

    const generateDigest = async () => {
        if (!hoa?.id) {
            // HOA still resolving on first portal load — wait for the
            // hoa?.id dependency to re-trigger this once it exists.
            setLoading(false);
            return;
        }
        if (windowDays === 'custom' && (!startDate || !endDate)) {
            return;
        }
        setLoading(true);
        try {
            const result = await insightDigest.generate(supabase, hoa.id, windowDays, startDate, endDate);
            setDigest(result);
            setLastGenerated(new Date());
        } catch (err) {
            console.error("InsightDigest generate() failed:", err, err?.stack);
        } finally {
            setLoading(false);
        }
    };

    const exportDigest = () => {
        if (!digest) return;
        const text = formatDigestAsText(digest);
        const blob = new Blob([text], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `HOA_Insight_Digest_${new Date().toISOString().split('T')[0]
            }.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const formatDigestAsText = (d) => {
        const lines = [
            `${hoaProfile(hoa).name.toUpperCase()} — AI INSIGHT DIGEST`,
            `Period: ${d.window_label}`,
            `Generated: ${new Date(d.generated_at)
                .toLocaleDateString()}`,
            ``,
            `── SUMMARY ──`,
            `Total queries: ${d.summary.total_queries}`,
            `Answer rate: ${d.summary.answer_rate}%`,
            `Unanswered: ${d.summary.unanswered}`,
            `Flagged queries: ${d.summary.flagged_queries}`,
            ``,
            `── TOP TOPICS ──`,
            ...d.top_topics.map(t =>
                `${t.label}: ${t.count} queries`
            ),
            ``,
        ];

        if (d.agenda_suggestions.length > 0) {
            lines.push(`── BOARD AGENDA SUGGESTIONS ──`);
            d.agenda_suggestions.forEach(s => {
                lines.push(`[${s.priority.toUpperCase()}] ${s.title}`);
                lines.push(s.detail);
                lines.push('');
            });
        }

        if (d.unanswered_queries.length > 0) {
            lines.push(`── UNANSWERED QUERIES ──`);
            d.unanswered_queries.forEach(q => {
                lines.push(`• ${q.query}`);
            });
        }

        return lines.join('\n');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center
        h-64 text-slate-400">
                <RefreshCw className="w-6 h-6 animate-spin mr-2" />
                Generating digest...
            </div>
        );
    }

    if (!digest) return null;

    const { summary, top_topics, unanswered_queries,
        agenda_suggestions, role_breakdown } = digest;

    return (
        <div className="space-y-6 p-4 md:p-6 bg-slate-50 min-h-full">

            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div>
                    <h2 className="font-display font-bold
            text-slate-800 text-lg">
                        Insight Digest
                    </h2>
                    <p className="text-sm text-slate-500 mt-0.5">
                        AI search activity analysis for board review
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2 lg:gap-3">
                    {/* Time window selector */}
                    <div className="relative">
                        <select
                            value={windowDays}
                            onChange={e => {
                                const val = e.target.value;
                                setWindowDays(val === 'custom' ? 'custom' : Number(val));
                            }}
                            className="appearance-none text-sm border
                border-amber-100/40 rounded-xl px-4 py-2
                pr-8 bg-white focus:outline-none
                focus:border-secondary cursor-pointer"
                        >
                            <option value={7}>Last 7 days</option>
                            <option value={30}>Last 30 days</option>
                            <option value={90}>Last 90 days</option>
                            <option value="custom">Custom Range</option>
                        </select>
                        <ChevronDown className="absolute right-2.5
              top-1/2 -translate-y-1/2 w-4 h-4
              text-slate-400 pointer-events-none" />
                    </div>
                    {windowDays === 'custom' && (
                        <div className="flex items-center gap-2">
                            <input
                                type="date"
                                value={startDate}
                                onChange={e => setStartDate(e.target.value)}
                                className="text-sm border border-amber-100/40 rounded-xl px-3 py-2 bg-white focus:outline-none focus:border-secondary"
                            />
                            <span className="text-slate-400 text-sm">to</span>
                            <input
                                type="date"
                                value={endDate}
                                onChange={e => setEndDate(e.target.value)}
                                className="text-sm border border-amber-100/40 rounded-xl px-3 py-2 bg-white focus:outline-none focus:border-secondary"
                            />
                            <button
                                type="button"
                                onClick={generateDigest}
                                disabled={!startDate || !endDate}
                                className="text-sm bg-secondary/10 text-secondary px-3 py-2 rounded-xl hover:bg-forest-200 disabled:opacity-50 transition-colors font-medium border border-forest-200"
                            >
                                Apply
                            </button>
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={() => {
                            if (windowDays === 30) {
                                generateDigest();
                            } else {
                                setWindowDays(30);
                            }
                            setStartDate('');
                            setEndDate('');
                        }}
                        className="p-2 text-slate-400 hover:text-secondary
              border border-amber-100/40 rounded-xl
              hover:border-forest-300 transition-colors"
                        title="Refresh"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                    <button
                        type="button"
                        onClick={exportDigest}
                        className="flex items-center gap-2 text-sm
              bg-secondary hover:brightness-110 text-white px-4 py-2
              rounded-xl transition-colors"
                    >
                        <Download className="w-4 h-4" />
                        Export
                    </button>
                </div>
            </div>

            {/* Empty state */}
            {summary.total_queries === 0 && (
                <div className="text-center py-16 border
          border-dashed border-amber-100/40 rounded-2xl">
                    <MessageSquare className="w-10 h-10 mx-auto
            mb-3 text-slate-300" />
                    <p className="text-slate-500 font-medium">
                        No queries yet
                    </p>
                    <p className="text-sm text-slate-400 mt-1">
                        Insight Digest will populate once homeowners
                        start using AI search.
                    </p>
                </div>
            )}

            {summary.total_queries > 0 && (
                <>
                    {/* Summary cards */}
                    <div className="grid grid-cols-2 gap-3
            sm:grid-cols-4">
                        {[
                            {
                                label: 'Total Queries',
                                value: summary.total_queries,
                                icon: MessageSquare,
                                color: 'text-secondary',
                                bg: 'bg-forest-50',
                            },
                            {
                                label: 'Answer Rate',
                                value: `${summary.answer_rate}%`,
                                icon: summary.answer_rate >= 70
                                    ? CheckCircle : XCircle,
                                color: summary.answer_rate >= 70
                                    ? 'text-secondary' : 'text-amber-600',
                                bg: summary.answer_rate >= 70
                                    ? 'bg-forest-50' : 'bg-amber-50',
                            },
                            {
                                label: 'Unanswered',
                                value: summary.unanswered,
                                icon: BookOpen,
                                color: summary.unanswered > 0
                                    ? 'text-amber-600' : 'text-secondary',
                                bg: summary.unanswered > 0
                                    ? 'bg-amber-50' : 'bg-forest-50',
                            },
                            {
                                label: 'Flagged',
                                value: summary.flagged_queries,
                                icon: AlertTriangle,
                                color: summary.high_risk_flags > 0
                                    ? 'text-red-600' : 'text-slate-500',
                                bg: summary.high_risk_flags > 0
                                    ? 'bg-red-50' : 'bg-slate-50',
                            },
                        ].map((card, i) => (
                            <div key={i} className="bg-white border border-amber-100/40
                rounded-2xl p-5 warm-tactile-shadow hover:shadow-md transition-shadow">
                                <div className={`w-8 h-8 rounded-lg ${card.bg}
                  flex items-center justify-center mb-2`}>
                                    <card.icon className={`w-4 h-4
                    ${card.color}`} />
                                </div>
                                <div className="text-2xl font-bold
                  text-slate-800">
                                    {card.value}
                                </div>
                                <div className="text-xs text-slate-500 mt-0.5">
                                    {card.label}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Board agenda suggestions */}
                    {agenda_suggestions.length > 0 && (
                        <div className="bg-white border border-amber-200
              rounded-2xl overflow-hidden warm-tactile-shadow">
                            <div className="px-4 py-3 bg-amber-50
                border-b border-amber-200 flex items-center
                gap-2">
                                <Calendar className="w-4 h-4
                  text-amber-600" />
                                <span className="text-sm font-semibold
                  text-amber-800">
                                    Board Agenda Suggestions
                                </span>
                                <span className="text-xs bg-amber-200
                  text-amber-700 px-2 py-0.5 rounded-full">
                                    {agenda_suggestions.length} item{agenda_suggestions.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                            <div className="divide-y divide-amber-100">
                                {agenda_suggestions.map((item, i) => (
                                    <div key={i} className="px-4 py-3">
                                        <div className="flex items-center
                      gap-2 mb-1">
                                            <span className={`text-xs font-medium
                        px-2 py-0.5 rounded-full
                        ${item.priority === 'high'
                                                    ? 'bg-red-100 text-red-700'
                                                    : 'bg-amber-100 text-amber-700'
                                                }`}>
                                                {item.priority}
                                            </span>
                                            <span className="text-sm font-semibold
                        text-slate-700">
                                                {item.title}
                                            </span>
                                        </div>
                                        <p className="text-sm text-slate-600">
                                            {item.detail}
                                        </p>
                                        {item.queries && (
                                            <div className="mt-2 space-y-1">
                                                {item.queries.map((q, j) => (
                                                    <p key={j} className="text-xs
                            text-slate-500 italic">
                                                        "{q}"
                                                    </p>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Top topics */}
                    {top_topics.length > 0 && (
                        <div className="bg-white border border-amber-100/40
              rounded-2xl overflow-hidden warm-tactile-shadow">
                            <div className="px-4 py-3 bg-slate-50
                border-b border-amber-100/40 flex items-center
                gap-2">
                                <TrendingUp className="w-4 h-4
                  text-secondary" />
                                <span className="text-sm font-semibold
                  text-slate-700">
                                    Top Topics
                                </span>
                            </div>
                            <div className="p-4 space-y-3">
                                {top_topics.map((topic, i) => {
                                    const maxCount = summary.total_queries;
                                    const pct = Math.round(
                                        (topic.count / maxCount) * 100
                                    );
                                    return (
                                        <div key={topic.id || i}>
                                            <button
                                                onClick={() => setSelectedTopic(topic)}
                                                className="w-full text-left flex items-center justify-between mb-1 hover:bg-slate-50 p-1 -mx-1 rounded-lg transition-colors"
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <span className="text-sm text-slate-700 font-medium">
                                                        {topic.label}
                                                    </span>
                                                    {topic.canonical && (
                                                        <p className="text-xs text-slate-400 italic truncate mt-0.5">
                                                            e.g. "{topic.canonical}"
                                                        </p>
                                                    )}
                                                </div>
                                                <span className="text-sm font-medium text-slate-500 shrink-0 ml-3">
                                                    {topic.count}
                                                    {topic.count === 1 ? ' query' : ' queries'}
                                                </span>
                                            </button>
                                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-3">
                                                <div
                                                    className="h-full bg-secondary rounded-full transition-all"
                                                    style={{ width: `${pct}%` }}
                                                />
                                            </div>
                                            {
                                                topic.sample_queries?.length > 1 && (
                                                    <div className="mb-3 -mt-1 space-y-0.5">
                                                        {topic.sample_queries.slice(1).map((q, j) => (
                                                            <p key={j} className="text-xs text-slate-400 pl-2 border-l-2 border-slate-100 italic">
                                                                "{q}"
                                                            </p>
                                                        ))}
                                                    </div>
                                                )
                                            }
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Unanswered queries */}
                    {unanswered_queries.length > 0 && (
                        <div className="bg-white border border-amber-100/40
              rounded-2xl overflow-hidden warm-tactile-shadow">
                            <div className="px-4 py-3 bg-slate-50
                border-b border-amber-100/40 flex items-center
                justify-between">
                                <div className="flex items-center gap-2">
                                    <BookOpen className="w-4 h-4
                    text-amber-500" />
                                    <span className="text-sm font-semibold
                    text-slate-700">
                                        Unanswered Queries
                                    </span>
                                </div>
                                <span className="text-xs text-slate-400">
                                    Upload relevant documents to answer these
                                </span>
                            </div>
                            <div className="divide-y divide-slate-100">
                                {unanswered_queries.map((q, i) => (
                                    <div key={i} className="px-4 py-2.5
                    flex items-center justify-between">
                                        <p className="text-sm text-slate-600
                      italic">
                                            "{q.query}"
                                        </p>
                                        <span className="text-xs text-slate-400
                      shrink-0 ml-3">
                                            {new Date(q.timestamp)
                                                .toLocaleDateString()}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Role breakdown */}
                    {Object.keys(role_breakdown).length > 0 && (
                        <div className="bg-white border border-amber-100/40
              rounded-2xl overflow-hidden warm-tactile-shadow">
                            <div className="px-4 py-3 bg-slate-50
                border-b border-amber-100/40 flex items-center
                gap-2">
                                <Users className="w-4 h-4
                  text-slate-500" />
                                <span className="text-sm font-semibold
                  text-slate-700">
                                    Queries by Role
                                </span>
                            </div>
                            <div className="p-4 space-y-2">
                                {Object.entries(role_breakdown)
                                    .sort((a, b) => b[1] - a[1])
                                    .map(([role, count]) => (
                                        <div key={role}
                                            className="flex items-center
                      justify-between">
                                            <span className="text-sm
                      text-slate-600 capitalize">
                                                {role}
                                            </span>
                                            <span className="text-sm font-medium
                      text-slate-700">
                                                {count}
                                            </span>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}

                    {/* Last generated */}
                    {lastGenerated && (
                        <p className="text-xs text-slate-400 text-right">
                            Generated {lastGenerated.toLocaleTimeString()}
                        </p>
                    )}
                </>
            )
            }

            {/* Topic Modal */}
            {
                selectedTopic && (
                    <div onClick={() => setSelectedTopic(null)} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
                        <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
                            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50 shrink-0">
                                <div>
                                    <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                                        <TrendingUp className="w-5 h-5 text-secondary" />
                                        {selectedTopic.label} Queries
                                    </h3>
                                    <p className="text-xs text-slate-500 mt-1">
                                        {selectedTopic.count} {selectedTopic.count === 1 ? 'query' : 'queries'} in the {windowDays === 7 ? 'last 7 days' : windowDays === 30 ? 'last 30 days' : `last ${windowDays} days`}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setSelectedTopic(null)}
                                    className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="p-6 overflow-y-auto space-y-4">
                                {selectedTopic.queries.map((q, idx) => (
                                    <div key={idx} className="border border-slate-100 rounded-xl p-4 bg-slate-50/50">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-xs font-semibold px-2 py-0.5 rounded uppercase tracking-wider bg-slate-200 text-slate-600">
                                                {q.user_role || 'Unknown'}
                                            </span>
                                            <span className="text-xs text-slate-400 font-medium">
                                                {new Date(q.timestamp).toLocaleString(undefined, {
                                                    month: 'short', day: 'numeric',
                                                    hour: 'numeric', minute: '2-digit'
                                                })}
                                            </span>
                                        </div>
                                        <p className="text-sm font-medium text-slate-800 border-l-2 border-forest-500 pl-3 py-1">
                                            "{q.query}"
                                        </p>
                                        {q.answer && (
                                            <div className="mt-3 p-3 bg-white border border-slate-100 rounded-lg warm-tactile-shadow">
                                                <p className="text-xs text-slate-600 leading-relaxed">
                                                    {q.answer}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default InsightDigest;
